import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, SignOut, UserCircle, List, X, Users, ShieldCheck } from "@phosphor-icons/react";

const navItems = [
  { to: "/", label: "Send" },
  { to: "/receive", label: "Receive" },
  { to: "/teams", label: "Teams" },
  { to: "/blog", label: "Blog" },
  { to: "/help", label: "Help" },
  { to: "/pricing", label: "Pricing" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <div className="w-7 h-7 bg-black grid place-items-center text-white font-black">S</div>
          <span className="font-mono font-bold text-lg tracking-tight text-black">SendMe<span className="text-[#FF3366]">.</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={`nav-${it.label.toLowerCase()}`}
              className={({ isActive }) => `px-3 py-2 text-sm uppercase tracking-[0.18em] border border-transparent brut-hover ${isActive ? "text-black border-black/15" : "text-black/55 hover:text-black"}`}>
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="hidden sm:inline-flex items-center gap-1 px-3 py-2 text-sm uppercase tracking-[0.18em] border border-[#FF3366] text-[#FF3366] brut-hover-accent">
                  <ShieldCheck size={16} weight="duotone"/> Admin
                </Link>
              )}
              <Link to="/profile" data-testid="nav-profile" className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm uppercase tracking-[0.18em] border border-black/15 brut-hover text-black">
                <UserCircle size={18} weight="duotone" /> {user.name?.split(" ")[0] || "Profile"}
              </Link>
              <button onClick={handleLogout} data-testid="logout-btn" className="btn-brut-ghost !py-2 !px-3" aria-label="Logout">
                <SignOut size={18} /> <span className="hidden sm:inline">Logout</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="login-btn" className="hidden sm:inline-flex btn-brut-ghost !py-2 !px-3">Sign in</Link>
              <Link to="/login" data-testid="register-btn" className="btn-brut-primary !py-2 !px-4">
                Sign in with Google <ArrowUpRight size={16} weight="bold" />
              </Link>
            </>
          )}
          <button onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-black/15 brut-hover text-black"
            aria-label="Toggle menu" aria-expanded={open}>
            {open ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/10 bg-white" data-testid="mobile-menu">
          <nav className="px-6 py-4 flex flex-col">
            {navItems.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.to === "/"} onClick={() => setOpen(false)} data-testid={`mobile-nav-${it.label.toLowerCase()}`}
                className={({ isActive }) => `py-3 text-sm uppercase tracking-[0.18em] border-b border-black/5 ${isActive ? "text-black" : "text-black/60 hover:text-black"}`}>
                {it.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to="/profile" onClick={() => setOpen(false)} className="py-3 text-sm uppercase tracking-[0.18em] border-b border-black/5">Profile</Link>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-3 text-sm uppercase tracking-[0.18em] border-b border-black/5 text-[#FF3366]">Admin</Link>}
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-sm uppercase tracking-[0.18em] border-b border-black/5">Sign in</Link>
                <Link to="/teams/join" onClick={() => setOpen(false)} className="py-3 text-sm uppercase tracking-[0.18em] text-[#FF3366]">Join a team</Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
